// libs
import { skipToken } from '@apollo/client/react'
import React, { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router'

// interfaces
import { GroupDetailFragmentFragment, NotificationSettingNotifier, useNotificationSettingsForSubject, useUpdateAllMediumsForNotificationSetting } from '@lopo-tech/lopo-api'

// components
import BackbuttonHeader from 'src/components/header/common/backbutton_header/backbutton_header'
import MoreMenu from 'src/components/header/subheaders/shared_contents/more_menu/more_menu'
import MenuItemSegment from 'src/components/item_segment/layouts/menu/menu'
import ToggleItemSegment from 'src/components/item_segment/layouts/toggle/toggle'
import { useShare } from 'src/components/share/lib/use_share'
import ShareButton from 'src/components/share/share_button/share_button'
import { ShareTargetList } from 'src/components/share/share_targets/share_targets'
import { showSnackBar } from 'src/components/snack_bar/snack_bar'

// constants
import { CLASS_PREFIX } from 'src/constants'
import { FRONTEND_GROUP_OVERVIEW_PATH } from 'src/constants/urls'

// helpers
import { textResources } from 'src/lang/de'
import { COLOR_TYPE_BLACK } from 'src/utils/color'
import initTracker, { HEADER_GROUP } from 'src/utils/reporting/events_tracking/events_tracking'
import { UrlUtils } from 'src/utils/url/url'

import {
  useGroupLeaveConfirmation
} from 'src/containers/smart_components/group_member_list/hooks/useGroupLeaveConfirmation'
import './header_group.scss'

const labels = textResources.groups
const cls: string = CLASS_PREFIX + 'header-group'
const clsMoreWrapper = `${cls}__more-wrapper`
const shareTracking = { tracker: initTracker(HEADER_GROUP), trackingObj: HEADER_GROUP }

interface Props {
  group: GroupDetailFragmentFragment | null
  showMemberCount?: boolean
  showMoreButton?: boolean
}

const HeaderGroup: React.FC<Props> = ({
  group,
  showMemberCount,
  showMoreButton
}) => {
  const history = useHistory()
  const [showShareList, setShowShareList] = useState(false)
  const { onGroupLeave, LeaveConfirmationModal } = useGroupLeaveConfirmation()

  const { data } = useNotificationSettingsForSubject(group?.id
    ? {
      variables: {
        notifier: NotificationSettingNotifier.NewGroupContent,
        subjectId: group.id
      }
    }
    : skipToken)
  const notificationSettings = data?.notificationSettingsForSubject

  const [updateNotificationSetting] =
    useUpdateAllMediumsForNotificationSetting()

  const groupNotificationsEnabled = notificationSettings?.some(
    (setting) => setting.isEnabled
  )

  const shareUrl = useMemo(() => (group ? UrlUtils.groupPage(group.slug) : ''), [group])
  const shareOptions = {
    text: labels.shareGroupMessage,
    title: group?.name,
    tracking: shareTracking,
    url: shareUrl,
    utmSource: 'group'
  }
  const { copyLink, openShareSheet, targets, track } = useShare(shareOptions)

  const handleBackButton = () => {
    UrlUtils.goBack(history, FRONTEND_GROUP_OVERVIEW_PATH)
  }

  const goToGroupsPage = () => {
    history.push(FRONTEND_GROUP_OVERVIEW_PATH)
  }

  const closeShareList = useCallback(() => setShowShareList(false), [])
  const onShare = useCallback(() => {
    openShareSheet(() => setShowShareList(true))
  }, [openShareSheet])

  const changeGroupNotificationSettings = useCallback(async () => {
    if (!group) return

    try {
      await updateNotificationSetting({
        variables: {
          enabled: !groupNotificationsEnabled,
          notifier: NotificationSettingNotifier.NewGroupContent,
          subjectId: group.id
        }
      })
      if (groupNotificationsEnabled) {
        showSnackBar(labels.notifications.turnNotificationOffSuccessMessage)
      } else {
        showSnackBar(labels.notifications.turnNotificationOnSuccessMessage)
      }
    } catch {
      showSnackBar(labels.notifications.changeError)
    }
  }, [group, groupNotificationsEnabled])

  return (
    <div className={cls}>
      {group && <BackbuttonHeader
        title={showMemberCount ? labels.memberCountHeader(group.confirmedMembersCount || 0) : group.name }
        handleBackButton={handleBackButton}
      >
        {showMoreButton && (
          <div className={clsMoreWrapper}>
            {group.ownGroupParticipation &&
              <>
                {group.ownGroupParticipation.role === 'ADMIN' &&
                    <ShareButton {...shareOptions} position='bottom' className={cls + '__share'} />
                }
                <MoreMenu onHide={closeShareList}>
                  {showShareList && (
                    <ShareTargetList targets={targets} onTarget={track} onCopyLink={copyLink} />
                  )}
                  {!showShareList && (
                    <ToggleItemSegment
                      label={labels.notifications.label}
                      isChecked={groupNotificationsEnabled}
                      onChange={changeGroupNotificationSettings}
                      icon={{
                        color: COLOR_TYPE_BLACK,
                        name: 'bell-new'
                      }}
                    />
                  )}
                  <MenuItemSegment
                    label={labels.shareGroup}
                    icon={{ name: 'share-thick' }}
                    navigation={{ onClick: onShare }}
                    hidden={showShareList}
                  />
                  <MenuItemSegment
                    label={labels.leaveGroup}
                    icon={{ name: 'close-circled' }}
                    navigation={{ onClick: () => { onGroupLeave(group, goToGroupsPage) } }}
                    hidden={showShareList}
                  />
                </MoreMenu>
              </>
            }
          </div>
        )}
      </BackbuttonHeader>}

      {LeaveConfirmationModal && <LeaveConfirmationModal />}
    </div>
  )
}

export default HeaderGroup
